// CustomerDetails.js
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import CustomerModal from '../../components/Modals/CustomerModal';
import AddInvoiceModal from '../../components/Modals/AddInvoiceModal';
import * as XLSX from 'xlsx';
import {
    Building2,
    Home,
    UserCheck,
    UserStar,
    MapPin,
    Phone,
    Mail,
    SquareChevronLeft,
    CreditCard,
    Users,
    Hotel,
    Plane,
    Car,
    Activity as ActivityIcon,
    Check,
    Wallet,
    Coins,
    Percent,
    FileText,
    Globe as LanguageIcon,
    Shield,
    ScrollText,
    Package,
    Plus,
    Download
} from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useTranslation } from '../../i18n/i18n';
import api from '../../api/axios';

const CustomerDetails = ({ lang }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation(lang);

    const [loading, setLoading] = useState(true);
    const [customer, setCustomer] = useState(null);
    const [associatedAccounts, setAssociatedAccounts] = useState([]);
    const [showCustomerModal, setShowCustomerModal] = useState(false);
    const [associatedProvider, setAssociatedProvider] = useState([]);
    const [showAddInvoiceModal, setShowAddInvoiceModal] = useState(false);
    const [invoices, setInvoices] = useState([]);
    const customer_url = process.env.REACT_APP_API_CUSTOMERS_URL
    const invoice_url = process.env.REACT_APP_API_INVOICE_URL;

    const [filters, setFilters] = useState({
        invoiceNumber: '',
        status: '',
        dateFrom: '2024-01-01',
        dateTo: '2026-12-31'
    });
    const fetchInvoices = async () => {
        try {
            // 1. Préparation du corps de la requête (filtres)
            const requestBody = {
                customerId: id,
                invoiceNumber: filters.invoiceNumber,
                status: filters.status,
                dateFrom: filters.dateFrom,
                dateTo: filters.dateTo
            };

            // Fonction utilitaire pour transformer une facture (pour éviter la répétition)
            const transformInvoice = (invoice) => ({
                id: invoice.id,
                number: invoice.invoice_number,
                date: invoice.invoice_at,
                status: invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1),
                amount: invoice.amount.toFixed(2),
                vat: '0.00',
                total: invoice.amount.toFixed(2),
                currency: invoice.currency,
                year: invoice.invoice_at ? new Date(invoice.invoice_at).getFullYear().toString() : 'N/A',
                customer_name: invoice.customer_name,
                paid_at: invoice.paid_at
            });

            // 2. Charger la PREMIÈRE PAGE pour obtenir les métadonnées (last_page)
            const firstResponse = await api.post(`${invoice_url}/customer/list`, requestBody, {
                params: { page: 1 }
            });

            const firstResult = firstResponse.data.result;
            const lastPage = firstResult.last_page; // On récupère le nombre total de pages

            // On initialise notre tableau de résultats avec les données de la page 1
            let allInvoices = firstResult.data.map(transformInvoice);

            // 3. Charger les AUTRES PAGES en parallèle (si elles existent)
            if (lastPage > 1) {
                const promises = [];

                for (let page = 2; page <= lastPage; page++) {
                    promises.push(
                        api.post(`${invoice_url}/customer/list`, requestBody, {
                            params: { page: page }
                        }).then(res => res.data.result.data.map(transformInvoice))
                    );
                }

                // Attendre que toutes les requêtes se terminent
                const pagesResults = await Promise.all(promises);

                // Fusionner les résultats de chaque page dans le tableau principal
                pagesResults.forEach(pageData => {
                    allInvoices.push(...pageData);
                });
            }

            // 4. Mettre à jour l'état final
            setInvoices(allInvoices);

        } catch (error) {
            console.error('Erreur lors du chargement des factures:', error);
            toast.error(t('errorLoadingInvoices'));
            setInvoices([]);
        }
    };

    // Fetch customer details from API
    const fetchcustomerDetails = async () => {
        try {
            setLoading(true);
            const response = await api.get(`${customer_url}/${id}`);
            const data = response.data.result;

            // Transform API data to match our structure
            const transformedcustomer = {
                id: data.id,
                name: data.name,
                email: data.activated_at, // Utiliser activated_at comme email
                phone: data.phone,
                address: data.address,
                city_id: data.city_id,
                country_id: data.country?.id,
                country_name: data.country?.name,
                postal_code: data.postal_code,
                notes: data.notes,
                language: data.language,
                currency: data.currency,
                commission_hotel: data.commission_hotel,
                commission_flight: data.commission_flight,
                commission_transfer: data.commission_transfer,
                commission_activity: data.commission_activity,
                deposit: data.deposit,
                balance: data.balance,
                pay_with_credit_card: data.pay_with_credit_card,
                pay_with_deposit: data.pay_with_deposit,
                confirm_refundable_booking: data.confirm_refundable_booking,
                has_activity: data.has_activity,
                has_hotel: data.has_hotel,
                has_transfer: data.has_transfer,
                has_flight: data.has_flight,
                has_booking: data.has_booking,
                doc_mandatory: data.doc_mandatory,
                doc_iata_agreement: data.doc_iata_agreement,
                doc_trade_licence: data.doc_trade_licence,
                customer_type_id: data.customer_type_id,
                website: data.website,
                status: data.status,
                provider: data.provider?.name,
                created_at: data.created_at,
                updated_at: data.updated_at
            };

            setCustomer(transformedcustomer);


            const transformedProvider = [{
                id: data.provider.id,
                name: data.provider.name,
                email: data.provider.email,
                type: data.provider.type.name,
                phone: data.provider.phone,
                status: data.provider.status,

            }]

            setAssociatedProvider(transformedProvider)
            console.log();

            // Charger les comptes associés
            const respo = await api.get(`${customer_url}/${id}/accounts`);
            const d = respo.data.result;

            // Transform contacts to associated accounts
            const transformedAccounts = d.map(contact => ({
                id: contact.id,
                name: contact.full_name,
                email: contact.email,
                phone: contact.phone,
                user_name: contact.user_name,
                super_admin: contact.is_super_admin,
                type: contact.type,
                status: contact.status
            })) || [];



            setAssociatedAccounts(transformedAccounts);

        } catch (error) {
            console.error('Erreur lors du chargement des détails client:', error);
            toast.error(t('errorLoadingCustomer'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (id) {
            fetchcustomerDetails();
            fetchInvoices();
        }
    }, [id]);

    const handleEdit = () => {
        setShowCustomerModal(true);
    };

    const formatBoolean = (value) => {
        return value ? t('yes') : t('no');
    };

    const formatPercentage = (value) => {
        if (value === null || value === undefined) return '0%';
        return `${value}%`;
    };

    const formatCurrency = (value) => {
        if (value === null || value === undefined) return '0';
        return new Intl.NumberFormat(lang, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(value);
    };

    const invoiceStats = {
        total: invoices.length,
        paid: invoices.filter(inv => inv.status.toLowerCase() === 'paid').length,
        pending: invoices.filter(inv => inv.status.toLowerCase() === 'unpaid').length,
        overdue: invoices.filter(inv => inv.status.toLowerCase() === 'overdue').length,

        totalAmount: invoices.reduce((sum, inv) => sum + Number(inv.amount), 0).toFixed(2),
    };

    const exportToExcel = () => {
        if (!invoices || invoices.length === 0) {
            toast.error(t('noDataToExport'));
            return;
        }

        try {
            // Calculer les statistiques pour l'export
            const invoiceStats = {
                totalInvoices: invoices.length,
                paidInvoices: invoices.filter(inv => inv.status.toLowerCase() === 'paid').length,
                unpaidInvoices: invoices.filter(inv => inv.status.toLowerCase() === 'unpaid').length,
                overdueInvoices: invoices.filter(inv => inv.status.toLowerCase() === 'overdue').length,
                totalAmount: invoices.reduce((sum, inv) => sum + parseFloat(inv.amount), 0).toFixed(2)
            };

            // Préparer les données pour Excel (dans l'ordre des colonnes du tableau)
            const excelData = invoices.map(invoice => ({
                [t('id')]: invoice.id,
                [t('invoiceNumber')]: invoice.number,
                [t('date')]: invoice.date,
                [t('status')]: invoice.status,
                [t('amount')]: invoice.amount,
                [t('paid_at')]: invoice.paid_at || t('notAvailable'),
                [t('currency')]: invoice.currency,
                [t('customer')]: invoice.customer_name || customer?.name || 'N/A'
            }));

            // Créer une feuille de calcul
            const ws = XLSX.utils.json_to_sheet(excelData);

            // Préparer les statistiques
            const statsData = [
                { [t('statistics')]: '' },
                { [t('statistics')]: t('totalInvoices'), '': invoiceStats.totalInvoices },
                { [t('statistics')]: t('paidInvoices'), '': invoiceStats.paidInvoices },
                { [t('statistics')]: t('unpaidInvoices'), '': invoiceStats.unpaidInvoices },
                { [t('statistics')]: t('overdueInvoices'), '': invoiceStats.overdueInvoices },
                { [t('statistics')]: t('totalAmount'), '': `${invoiceStats.totalAmount} ${invoices[0]?.currency || 'EUR'}` },
                { [t('statistics')]: '' },
                { [t('statistics')]: t('supplierInformation'), '': '' },
                { [t('statistics')]: t('name'), '': customer?.name || 'N/A' },
                { [t('statistics')]: t('email'), '': customer?.email || 'N/A' },
                { [t('statistics')]: t('phone'), '': customer?.phone || 'N/A' },
                { [t('statistics')]: t('providerType'), '': customer?.providerType || 'N/A' },
                { [t('statistics')]: '' },
                { [t('statistics')]: t('exportInformation'), '': '' },
                { [t('statistics')]: t('exportDate'), '': new Date().toLocaleString(lang) },
                { [t('statistics')]: t('filtersApplied'), '': '' },
                { [t('statistics')]: t('dateFrom'), '': filters.dateFrom || t('none') },
                { [t('statistics')]: t('dateTo'), '': filters.dateTo || t('none') },
                { [t('statistics')]: t('statusFilter'), '': filters.status ? t(filters.status) : t('allStatuses') },
                { [t('statistics')]: t('invoiceNumberFilter'), '': filters.invoiceNumber || t('none') },
            ];

            const wsStats = XLSX.utils.json_to_sheet(statsData, { skipHeader: true });

            // Créer un nouveau classeur avec plusieurs feuilles
            const wb = XLSX.utils.book_new();

            // Ajouter la feuille des données des factures
            XLSX.utils.book_append_sheet(wb, ws, t('invoices'));

            // Ajouter la feuille des statistiques
            XLSX.utils.book_append_sheet(wb, wsStats, t('statistics'));

            // Mettre en forme les colonnes (largeur automatique)
            if (ws['!ref']) {
                const range = XLSX.utils.decode_range(ws['!ref']);
                const wscols = [];
                for (let C = range.s.c; C <= range.e.c; ++C) {
                    let maxLength = 0;
                    for (let R = range.s.r; R <= range.e.r; ++R) {
                        const cell = ws[XLSX.utils.encode_cell({ c: C, r: R })];
                        if (cell && cell.v) {
                            const cellLength = cell.v.toString().length;
                            if (cellLength > maxLength) maxLength = cellLength;
                        }
                    }
                    wscols.push({ wch: Math.min(maxLength + 2, 40) }); // Max 40 caractères
                }
                ws['!cols'] = wscols;
            }

            // Mettre en forme les statistiques
            if (wsStats['!ref']) {
                const range = XLSX.utils.decode_range(wsStats['!ref']);
                const wscolsStats = [
                    { wch: 30 }, // Première colonne (labels)
                    { wch: 25 }  // Deuxième colonne (valeurs)
                ];
                wsStats['!cols'] = wscolsStats;
            }

            // Générer le nom du fichier avec la date et le nom du fournisseur
            const date = new Date();
            const dateStr = date.toISOString().split('T')[0];
            const timeStr = date.toTimeString().split(' ')[0].replace(/:/g, '-');
            const supplierName = customer?.name?.replace(/[^a-z0-9]/gi, '_').toLowerCase() || 'customer';
            const fileName = `invoices_${supplierName}_${dateStr}_${timeStr}.xlsx`;

            // Télécharger le fichier
            XLSX.writeFile(wb, fileName);

            toast.success(t('exportSuccess'));

        } catch (error) {
            console.error('Erreur lors de l\'export Excel:', error);
            toast.error(t('exportError'));
        }
    };



    const styles = {
        container: {
            padding: '8px 24px',
            background: '#f9fafb',
            minHeight: '100vh'
        },
        header: {
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            marginBottom: '8px'
        },
        backButton: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 12px',
            backgroundColor: 'transparent',
            color: '#4b5563',
            border: '1px solid #d1d5db',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            borderRadius: '8px',
            transition: 'all 0.2s ease',
            marginBottom: '4px',
            '&:hover': {
                backgroundColor: '#f3f4f6',
                borderColor: '#9ca3af'
            }
        },
        title: {
            fontSize: '28px',
            fontWeight: 'bold',
            color: '#1f2937',
            margin: 0
        },
        loadingContainer: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px'
        },
        loadingText: {
            fontSize: '18px',
            color: '#6b7280'
        },
        section: {
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        },
        sectionHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px',
            paddingBottom: '16px',
            borderBottom: '1px solid #e5e7eb'
        },
        sectionTitle: {
            fontSize: '20px',
            fontWeight: '600',
            color: '#1f2937',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            margin: '16px 0px'
        },
        statusRow: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px',
            padding: '16px',
            background: '#f0fdf4',
            borderRadius: '8px',
            border: '1px solid #dcfce7'
        },
        status: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '16px',
            fontWeight: '600'
        },
        infoGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '20px'
        },
        infoItem: {
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            padding: '16px',
            background: '#f9fafb',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            transition: 'all 0.2s ease',
            '&:hover': {
                background: '#f3f4f6',
                transform: 'translateY(-2px)',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }
        },
        infoItemHeader: {
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            marginBottom: '4px'
        },
        infoIcon: {
            color: '#10b981' // Vert au lieu de violet
        },
        infoLabel: {
            fontSize: '12px',
            color: '#6b7280',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
        },
        infoValue: {
            fontSize: '16px',
            color: '#1f2937',
            fontWeight: '500',
            lineHeight: '1.5'
        },
        infoSubValue: {
            fontSize: '14px',
            color: '#4b5563',
            fontWeight: '400'
        },
        buttonGroup: {
            display: 'flex',
            gap: '12px',
            alignItems: 'center'
        },
        primaryButton: {
            padding: '10px 20px',
            background: '#10b981', // Vert
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontWeight: '500',
            transition: 'all 0.2s ease',
            '&:hover': {
                background: '#0da271', // Vert plus foncé
                transform: 'translateY(-1px)'
            }
        },
        secondaryButton: {
            padding: '10px 20px',
            background: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontWeight: '500',
            transition: 'all 0.2s ease',
            '&:hover': {
                background: '#2563eb',
                transform: 'translateY(-1px)'
            }
        },
        statusButton: {
            padding: '10px 20px',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '14px',
            transition: 'all 0.2s ease',
            background: (customer?.status === 'active') ? '#ef4444' :
                (customer?.status === 'inactive') ? '#10b981' : '#3b82f6',
            color: 'white',
            '&:hover': {
                opacity: '0.9',
                transform: 'translateY(-1px)'
            }
        },
        invoiceSummary: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
      gap: '16px',
      marginBottom: '24px',
      padding: '15px',
      background: '#7c3aed',
      borderRadius: '12px',
      color: 'white'
    },
    summaryItem: {
      textAlign: 'center',
      padding: '4px',
      background: 'rgba(255, 255, 255, 0.2)',
      borderRadius: '8px',
      backdropFilter: 'blur(10px)'
    },
    summaryValue: {
      fontSize: '28px',
      fontWeight: 'bold',
      marginBottom: '4px',
      color: 'white'
    },
        summaryLabel: {
            fontSize: '14px',
            opacity: '0.9',
            color: 'white'
        },
        noData: {
            textAlign: 'center',
            padding: '60px 20px',
            color: '#6b7280',
            background: '#f9fafb',
            borderRadius: '8px',
            border: '2px dashed #d1d5db'
        },
        simpleTableContainer: {
            overflowX: 'auto',
            borderRadius: '12px',
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        },
        simpleTable: {
            width: '100%',
            borderCollapse: 'separate',
            borderSpacing: '0',
            background: 'white'
        },
        tableHeader: {
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' // Dégradé vert
        },
        tableHeaderCell: {
            padding: '16px',
            textAlign: 'left',
            color: 'white',
            fontWeight: '700',
            fontSize: '16px',
            borderBottom: '1px solid rgba(255,255,255,0.1)',
        },
        tableRow: {
            borderBottom: '1px solid #e5e7eb',
            transition: 'background 0.2s ease',
            '&:hover': {
                background: '#f9fafb'
            }
        },
        tableCell: {
            padding: '16px',
            fontSize: '14px',
            color: '#374151',
            borderBottom: '1px solid #e5e7eb'
        },
        statusBadge: {
            padding: '6px 12px',
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: '600',
            color: 'white',
            textTransform: 'uppercase',
            display: 'inline-block',
            minWidth: '80px',
            textAlign: 'center'
        },
        actionButtons: {
            display: 'flex',
            gap: '8px',
        },
        editButton: {
            padding: '8px 16px',
            background: '#10b981', // Vert
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '12px',
            cursor: 'pointer',
            fontWeight: '500',
            transition: 'all 0.2s ease',
        },
        deleteButton: {
            padding: '8px 16px',
            background: '#ef4444',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '12px',
            cursor: 'pointer',
            fontWeight: '500',
            transition: 'all 0.2s ease',
        },
        totalRow: {
            background: '#f8fafc',
            borderTop: '2px solid #e5e7eb',
            fontWeight: '600'
        },
        totalCell: {
            padding: '16px',
            color: '#1f2937',
            fontSize: '15px'
        },
        contactBadge: {
            padding: '4px 12px',
            borderRadius: '20px',
            fontSize: '11px',
            fontWeight: '600',
            color: 'white',
            background: '#10b981', // Vert
            marginLeft: '8px'
        },
        commissionGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '12px',
            marginBottom: '16px'
        },
        commissionItem: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '12px',
            background: '#f0fdf4',
            borderRadius: '8px',
            border: '1px solid #dcfce7'
        },
        commissionLabel: {
            fontSize: '12px',
            color: '#065f46',
            fontWeight: '600',
            marginBottom: '4px'
        },
        commissionValue: {
            fontSize: '18px',
            color: '#10b981',
            fontWeight: 'bold'
        },
        featuresGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
            gap: '12px',
            marginBottom: '16px'
        },
        featureItem: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '4px 0px 4px 8px',
            background: '#f8fafc',
            borderRadius: '6px',
            border: '1px solid #e5e7eb',
        },
        featureIcon: {
            color: '#10b981'
        },
        featureText: {
            fontSize: '14px',
            padding: '8px 0px',
            color: '#374151'
        }
    };

    if (loading) {
        return (
            <div style={styles.container}>
                <button
                    onClick={() => navigate('/customers')}
                    style={styles.backButton}
                >
                    <SquareChevronLeft size={20} />
                    {t('backToCustomers')}
                </button>
                <div style={styles.loadingContainer}>
                    <div style={styles.loadingText}>{t('loading')}...</div>
                </div>
            </div>
        );
    }

    if (!customer) {
        return (
            <div style={styles.container}>
                <button
                    onClick={() => navigate('/customers')}
                    style={styles.backButton}
                >
                    <SquareChevronLeft size={20} />
                    {t('backToCustomers')}
                </button>
                <div style={styles.noData}>
                    <p>{t('customerNotFound')}</p>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            {/* Header avec bouton retour */}
            <div style={styles.header}>
                <button
                    onClick={() => navigate('/customers')}
                    style={styles.backButton}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f3f4f6';
                        e.currentTarget.style.borderColor = '#9ca3af';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.borderColor = '#d1d5db';
                    }}
                >
                    <SquareChevronLeft size={20} />
                    {t('backToCustomers')}
                </button>
            </div>

            {/* Section Informations du client */}
            <div style={styles.section}>
                <div style={styles.sectionHeader}>
                    <h2 style={styles.sectionTitle}>
                        <Building2 size={24} color="#10b981" />
                        {t('customerInformation')}
                    </h2>
                    <button
                        style={styles.primaryButton}
                        onClick={handleEdit}
                    >
                        {t('edit')}
                    </button>
                </div>

                {/* Informations de base */}
                <div style={styles.sectionTitle}>
                    <Home size={20} />
                    {t('basicInformation')}
                </div>
                <div style={styles.infoGrid}>
                    {/* Nom */}
                    <div style={styles.infoItem}>
                        <div style={styles.infoItemHeader}>
                            <Building2 size={18} style={styles.infoIcon} />
                            <span style={styles.infoLabel}>{t('name')}</span>
                        </div>
                        <span style={styles.infoValue}>{customer.name || t('notAvailable')}</span>
                    </div>

                    {/* Téléphone */}
                    <div style={styles.infoItem}>
                        <div style={styles.infoItemHeader}>
                            <Phone size={18} style={styles.infoIcon} />
                            <span style={styles.infoLabel}>{t('phone')}</span>
                        </div>
                        <span style={styles.infoValue}>{customer.phone || t('notAvailable')}</span>
                    </div>

                    {/* Adresse */}
                    <div style={styles.infoItem}>
                        <div style={styles.infoItemHeader}>
                            <MapPin size={18} style={styles.infoIcon} />
                            <span style={styles.infoLabel}>{t('address')}</span>
                        </div>
                        <span style={styles.infoValue}>{customer.address || t('notAvailable')}</span>
                        <span style={styles.infoSubValue}>
                            {customer.postal_code || ''} {customer.country_name || ''}
                        </span>
                    </div>

                    {/* Langue */}
                    <div style={styles.infoItem}>
                        <div style={styles.infoItemHeader}>
                            <LanguageIcon size={18} style={styles.infoIcon} />
                            <span style={styles.infoLabel}>{t('language')}</span>
                        </div>
                        <span style={styles.infoValue}>{customer.language || t('notAvailable')}</span>
                    </div>
                </div>

                {/* Commissions */}
                <div style={styles.sectionTitle}>
                    <Percent size={20} />
                    {t('commissions')}
                </div>
                <div style={styles.commissionGrid}>
                    {/* Commission Hôtel */}
                    <div style={styles.commissionItem}>
                        <span style={styles.commissionLabel}>
                            <Hotel size={14} /> {t('commissionHotel')}
                        </span>
                        <span style={styles.commissionValue}>{formatPercentage(customer.commission_hotel)}</span>
                    </div>

                    {/* Commission Vol */}
                    <div style={styles.commissionItem}>
                        <span style={styles.commissionLabel}>
                            <Plane size={14} /> {t('commissionFlight')}
                        </span>
                        <span style={styles.commissionValue}>{formatPercentage(customer.commission_flight)}</span>
                    </div>

                    {/* Commission Transfert */}
                    <div style={styles.commissionItem}>
                        <span style={styles.commissionLabel}>
                            <Car size={14} /> {t('commissionTransfer')}
                        </span>
                        <span style={styles.commissionValue}>{formatPercentage(customer.commission_transfer)}</span>
                    </div>

                    {/* Commission Activité */}
                    <div style={styles.commissionItem}>
                        <span style={styles.commissionLabel}>
                            <ActivityIcon size={14} /> {t('commissionActivity')}
                        </span>
                        <span style={styles.commissionValue}>{formatPercentage(customer.commission_activity)}</span>
                    </div>
                </div>

                {/* Finance */}
                <div style={styles.sectionTitle}>
                    <Wallet size={20} />
                    {t('financialInformation')}
                </div>
                <div style={styles.infoGrid}>
                    {/* Dépôt */}
                    <div style={styles.infoItem}>
                        <div style={styles.infoItemHeader}>
                            <Wallet size={18} style={styles.infoIcon} />
                            <span style={styles.infoLabel}>{t('deposit')}</span>
                        </div>
                        <span style={styles.infoValue}>{formatCurrency(customer.deposit)}</span>
                    </div>

                    {/* Solde */}
                    <div style={styles.infoItem}>
                        <div style={styles.infoItemHeader}>
                            <Coins size={18} style={styles.infoIcon} />
                            <span style={styles.infoLabel}>{t('balance')}</span>
                        </div>
                        <span style={styles.infoValue}>{formatCurrency(customer.balance)}</span>
                    </div>
                </div>

                {/* Fonctionnalités */}
                <div style={styles.sectionTitle}>
                    <Package size={20} />
                    {t('features')}
                </div>
                <div style={styles.featuresGrid}>
                    <div style={styles.featureItem}>
                        <Hotel size={16} style={styles.featureIcon} />
                        <span style={styles.featureText}>{t('hasHotel')}: {formatBoolean(customer.has_hotel)}</span>
                    </div>
                    <div style={styles.featureItem}>
                        <Plane size={16} style={styles.featureIcon} />
                        <span style={styles.featureText}>{t('hasFlight')}: {formatBoolean(customer.has_flight)}</span>
                    </div>
                    <div style={styles.featureItem}>
                        <Car size={16} style={styles.featureIcon} />
                        <span style={styles.featureText}>{t('hasTransfer')}: {formatBoolean(customer.has_transfer)}</span>
                    </div>
                    <div style={styles.featureItem}>
                        <ActivityIcon size={16} style={styles.featureIcon} />
                        <span style={styles.featureText}>{t('hasActivity')}: {formatBoolean(customer.has_activity)}</span>
                    </div>
                    <div style={styles.featureItem}>
                        <Building2 size={16} style={styles.featureIcon} />
                        <span style={styles.featureText}>{t('hasBooking')}: {formatBoolean(customer.has_booking)}</span>
                    </div>
                </div>

                {/* Options de paiement */}
                <div style={styles.sectionTitle}>
                    <CreditCard size={20} />
                    {t('paymentOptions')}
                </div>
                <div style={styles.featuresGrid}>
                    <div style={styles.featureItem}>
                        <CreditCard size={16} style={styles.featureIcon} />
                        <span style={styles.featureText}>{t('payWithCreditCard')}: {formatBoolean(customer.pay_with_credit_card)}</span>
                    </div>
                    <div style={styles.featureItem}>
                        <Wallet size={16} style={styles.featureIcon} />
                        <span style={styles.featureText}>{t('payWithDeposit')}: {formatBoolean(customer.pay_with_deposit)}</span>
                    </div>
                    <div style={styles.featureItem}>
                        <Check size={16} style={styles.featureIcon} />
                        <span style={styles.featureText}>{t('confirmRefundableBooking')}: {formatBoolean(customer.confirm_refundable_booking)}</span>
                    </div>
                </div>

                {/* Documents */}
                <div style={styles.sectionTitle}>
                    <FileText size={20} />
                    {t('documents')}
                </div>
                <div style={styles.featuresGrid}>
                    <div style={styles.featureItem}>
                        <Shield size={16} style={styles.featureIcon} />
                        <span style={styles.featureText}>{t('docMandatory')}: {formatBoolean(customer.doc_mandatory)}</span>
                    </div>
                    <div style={styles.featureItem}>
                        <Shield size={16} style={styles.featureIcon} />
                        <span style={styles.featureText}>{t('docIataAgreement')}: {formatBoolean(customer.doc_iata_agreement)}</span>
                    </div>
                    <div style={styles.featureItem}>
                        <Shield size={16} style={styles.featureIcon} />
                        <span style={styles.featureText}>{t('docTradeLicence')}: {formatBoolean(customer.doc_trade_licence)}</span>
                    </div>
                </div>

                {/* Notes */}
                <div style={styles.sectionTitle}>
                    <ScrollText size={20} />
                    {t('notes')}
                </div>
                <div style={styles.infoItem}>
                    <div style={styles.infoItemHeader}>
                        <FileText size={18} style={styles.infoIcon} />
                        <span style={styles.infoLabel}>{t('notes')}</span>
                    </div>
                    <span style={styles.infoValue}>{customer.notes || t('noNotes')}</span>
                </div>
            </div>

            {/* Section Comptes associés */}
            <div style={styles.section}>
                <div style={styles.sectionHeader}>
                    <h2 style={styles.sectionTitle}>
                        <UserCheck size={24} color="#10b981" />
                        {t('associatedAccounts')}
                        <span style={{
                            fontSize: '14px',
                            color: '#6b7280',
                            marginLeft: '8px'
                        }}>
                            ({associatedAccounts.length})
                        </span>
                    </h2>
                </div>

                {associatedAccounts.length > 0 ? (
                    <div style={styles.simpleTableContainer}>
                        <table style={styles.simpleTable}>
                            <thead>
                                <tr style={styles.tableHeader}>
                                    <th style={styles.tableHeaderCell}>{t('id')}</th>
                                    <th style={styles.tableHeaderCell}>{t('name')}</th>
                                    <th style={styles.tableHeaderCell}>{t('email')}</th>
                                    <th style={styles.tableHeaderCell}>{t('phone')}</th>
                                    <th style={styles.tableHeaderCell}>{t('userName')}</th>
                                    <th style={styles.tableHeaderCell}>{t('status')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {associatedAccounts.map((account) => (
                                    <tr
                                        key={account.id}
                                        style={{
                                            ...styles.tableRow,
                                            cursor: 'pointer',
                                            transition: 'background-color 0.2s ease'
                                        }}
                                        onClick={() => navigate(`/users/${account.id}`)}
                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#d1fae5'}
                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                    >
                                        <td style={styles.tableCell}>{account.id}</td>
                                        <td style={styles.tableCell}>
                                            {account.name}
                                            {account.super_admin && (
                                                <span style={styles.contactBadge} title={t('admin')}>
                                                    {t('admin')}
                                                </span>
                                            )}
                                        </td>
                                        <td style={styles.tableCell}>{account.email}</td>
                                        <td style={styles.tableCell}>{account.phone}</td>
                                        <td style={styles.tableCell}>{account.user_name}</td>
                                        <td style={styles.tableCell}>
                                            <span style={{
                                                ...styles.statusBadge,
                                                background: account.status === 1 ? '#10b981' : '#6b7280'
                                            }}>
                                                {account.status === 1 ? 'ENABLED' : 'DISABLED'}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div style={styles.noData}>
                        <UserCheck size={48} color="#9ca3af" />
                        <p style={{ marginTop: '16px' }}>{t('noAssociatedAccounts')}</p>
                    </div>
                )}

            </div>

            {/* Section fournisseurs associés */}
            <div style={styles.section}>
                <div style={styles.sectionHeader}>
                    <h2 style={styles.sectionTitle}>
                        <UserStar size={24} color="#10b981" />
                        {t('associatedProvider')}
                        <span style={{
                            fontSize: '14px',
                            color: '#6b7280',
                            marginLeft: '8px'
                        }}>
                            ({associatedProvider.length})
                        </span>
                    </h2>
                </div>

                {associatedProvider.length > 0 ? (
                    <div style={styles.simpleTableContainer}>
                        <table style={styles.simpleTable}>
                            <thead>
                                <tr style={styles.tableHeader}>
                                    <th style={styles.tableHeaderCell}>{t('id')}</th>
                                    <th style={styles.tableHeaderCell}>{t('name')}</th>
                                    <th style={styles.tableHeaderCell}>{t('email')}</th>
                                    <th style={styles.tableHeaderCell}>{t('phone')}</th>
                                    <th style={styles.tableHeaderCell}>{t('type')}</th>
                                    <th style={styles.tableHeaderCell}>{t('status')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {associatedProvider.map((account) => (
                                    <tr
                                        key={account.id}
                                        style={{
                                            ...styles.tableRow,
                                            cursor: 'pointer',
                                            transition: 'background-color 0.2s ease'
                                        }}
                                        onClick={() => navigate(`/suppliers/${account.id}`)}
                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#d1fae5'}
                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                    >
                                        <td style={styles.tableCell}>{account.id}</td>
                                        <td style={styles.tableCell}>
                                            {account.name}
                                            {account.super_admin && (
                                                <span style={styles.contactBadge} title={t('admin')}>
                                                    {t('admin')}
                                                </span>
                                            )}
                                        </td>
                                        <td style={styles.tableCell}>{account.email}</td>
                                        <td style={styles.tableCell}>{account.phone}</td>
                                        <td style={styles.tableCell}>{account.type}</td>
                                        <td style={styles.tableCell}>
                                            <span style={{
                                                ...styles.statusBadge,
                                                background: account.status === 'enabled' ? '#10b981' : '#6b7280'
                                            }}>
                                                {account.status}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div style={styles.noData}>
                        <UserStar size={48} color="#9ca3af" />
                        <p style={{ marginTop: '16px' }}>{t('noAssociatedProvider')}</p>
                    </div>
                )}

            </div>

            {/* Section Factures (conservée) */}
            <div style={styles.section}>
                <div style={styles.sectionHeader}>
                    <h2 style={styles.sectionTitle}>
                        <FileText size={24} color="#7c3aed" />
                        {t('invoices')}
                        <span style={{
                            fontSize: '14px',
                            color: '#6b7280',
                            marginLeft: '8px'
                        }}>
                            ({invoices.length})
                        </span>
                    </h2>

                    {invoices.length > 0 ? (
                        <div style={styles.buttonGroup}>
                            <button
                                style={styles.primaryButton}
                                onClick={exportToExcel}
                            >
                                <Download size={16} />
                                {t('export')}
                            </button>
                            <button
                                style={styles.secondaryButton}
                                onClick={() => setShowAddInvoiceModal(true)}
                            >
                                <Plus size={16} />
                                {t('addInvoice')}
                            </button>
                        </div>
                    ) : (
                        null
                    )}
                </div>

                {/* Résumé des factures amélioré */}
                {invoices.length > 0 && (
                    <div style={styles.invoiceSummary}>
                        <div style={styles.summaryItem}>
                            <div style={styles.summaryValue}>{invoiceStats.total}</div>
                            <div style={styles.summaryLabel}>{t('totalInvoices')}</div>
                        </div>
                        <div style={styles.summaryItem}>
                            <div style={styles.summaryValue}>{invoiceStats.paid}</div>
                            <div style={styles.summaryLabel}>{t('paidInvoices')}</div>
                        </div>
                        <div style={styles.summaryItem}>
                            <div style={styles.summaryValue}>{invoiceStats.pending}</div>
                            <div style={styles.summaryLabel}>{t('pendingInvoices')}</div>
                        </div>
                        <div style={styles.summaryItem}>
                            <div style={styles.summaryValue}>{invoiceStats.overdue}</div>
                            <div style={styles.summaryLabel}>{t('overdueInvoices')}</div>
                        </div>
                    </div>
                )}

                {invoices.length > 0 ? (
                    <div style={styles.simpleTableContainer}>
                        <table style={styles.simpleTable}>
                            <thead>
                                <tr style={{ ...styles.tableHeader, background: '#7c3aed' }}>
                                    <th style={styles.tableHeaderCell}>{t('id')}</th>
                                    <th style={styles.tableHeaderCell}>{t('invoiceNumber')}</th>
                                    <th style={styles.tableHeaderCell}>{t('date')}</th>
                                    <th style={styles.tableHeaderCell}>{t('status')}</th>
                                    <th style={styles.tableHeaderCell}>{t('amount')}</th>
                                    <th style={styles.tableHeaderCell}>{t('paid_at')}</th>
                                    <th style={styles.tableHeaderCell}>{t('currency')}</th>
                                    <th style={styles.tableHeaderCell}>{t('customer')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {invoices.map((invoice) => (
                                    <tr key={invoice.id}
                                        style={{
                                            ...styles.tableRow,
                                            cursor: 'pointer',
                                            transition: 'background-color 0.2s ease'
                                        }}
                                        onClick={() => navigate(`/invoices/suppliers/${invoice.id}`)}
                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#667eea22'}
                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                    >
                                        <td style={styles.tableCell} title={invoice.id}>{invoice.id}</td>
                                        <td style={styles.tableCell} title={invoice.number}>
                                            <strong>{invoice.number}</strong>
                                        </td>
                                        <td style={styles.tableCell} title={invoice.date}>{invoice.date}</td>
                                        <td style={styles.tableCell} title={invoice.status}>
                                            <span style={{
                                                ...styles.statusBadge,
                                                background: invoice.status === 'Paid' ? '#10b981' :
                                                    invoice.status === 'Unpaid' ? '#f59e0b' : '#ef4444'
                                            }}>
                                                {invoice.status}
                                            </span>
                                        </td>
                                        <td style={styles.tableCell} title={invoice.amount}>{invoice.amount}</td>
                                        <td style={styles.tableCell} title={invoice.paid_at}>
                                            <strong>{invoice.paid_at || t('notAvailable')}</strong>
                                        </td>
                                        <td style={styles.tableCell} title={invoice.currency}>{invoice.currency}</td>
                                        <td style={styles.tableCell} title={invoice.customer_name}>{invoice.customer_name}</td>

                                    </tr>
                                ))}
                                {/* Ligne des totaux */}
                                <tr style={styles.totalRow}>
                                    <td style={styles.totalCell} colSpan="4">
                                        <strong>{t('totals')}:</strong>
                                    </td>
                                    <td style={styles.totalCell}>
                                        <strong>{invoiceStats.totalAmount} </strong>
                                    </td>
                                    <td style={styles.totalCell} colSpan="3"></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div style={styles.noData}>
                        <FileText size={48} color="#9ca3af" />
                        <p style={{ marginTop: '16px' }}>{t('noInvoices')}</p>
                        <button style={{
                            ...styles.primaryButton,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                            onClick={() => setShowAddInvoiceModal(true)}>
                            <Plus size={16} />
                            {t('createFirstInvoice')}
                        </button>
                    </div>
                )}
            </div>

            {showCustomerModal && (
                <CustomerModal
                    customer={customer}
                    type={'edit'}
                    onClose={() => setShowCustomerModal(false)}
                    onSave={() => {
                        fetchcustomerDetails();
                        toast.success(t('customerUpdatedSuccess'));
                        setShowCustomerModal(false);
                    }}
                    lang={lang}
                />
            )}

            {showAddInvoiceModal && (
                <AddInvoiceModal
                    onClose={() => setShowAddInvoiceModal(false)}
                    onSuccess={(newInvoice) => {
                        // Rafraîchir la liste des factures
                        fetchInvoices();
                        toast.success(t('invoiceCreatedSuccess'));
                        setShowAddInvoiceModal(false);
                    }}
                    lang={lang}
                />
            )}

            <ToastContainer position="bottom-right" />
        </div>
    );
};

export default CustomerDetails;